import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { get } from '@/api/client';

/**
 * 메인 페이지 최상단 시장상황 요약 배너.
 * - GET /market-reports/latest 로 created_at DESC LIMIT 1 조회
 * - 카테고리별 아이콘 + 라벨 + 주요 지수(타이틀에서 파싱) + summary 한 줄
 * - 클릭 시 /market/:id 로 이동
 */

interface MarketReport {
  id: number;
  category: string;
  title: string;
  summary: string;
  report_date: string;
  created_at: string;
}

interface CategoryMeta {
  label: string;
  /** 타이틀에서 추출할 지수 키 (없으면 summary 만 표시) */
  tickers: string[];
}

const CATEGORY_META: Record<string, CategoryMeta> = {
  morning_brief: { label: '모닝브리핑', tickers: [] },
  intraday:      { label: '장중브리핑', tickers: ['KOSPI', 'KOSDAQ'] },
  kr_close:      { label: '국장마감',   tickers: ['KOSPI', 'KOSDAQ'] },
  us_close:      { label: '미장마감',   tickers: ['S&P', 'NASDAQ'] },
  coin_24h:      { label: '코인24H',    tickers: ['BTC', 'ETH'] },
};

interface ParsedIndex {
  ticker: string;
  value: string;
  rate: number;
}

/** 타이틀에서 "TICKER VALUE (±RATE%)" 패턴 추출. value 는 $, 콤마, 소수점 포함 가능. */
function parseIndex(title: string, ticker: string): ParsedIndex | null {
  const escaped = ticker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`${escaped}\\s+(\\$?[\\d,\\.]+)\\s*\\(([+-]?[\\d.]+)%\\)`);
  const m = title.match(re);
  if (!m) return null;
  return { ticker, value: m[1], rate: parseFloat(m[2]) };
}

function rateColor(rate: number): string {
  if (rate > 0) return 'text-[#fca5a5]'; // 밝은 빨강 (다크 배경)
  if (rate < 0) return 'text-[#93c5fd]'; // 밝은 파랑 (다크 배경)
  return 'text-white/80';
}

function formatRate(rate: number): string {
  const sign = rate > 0 ? '+' : '';
  return `${sign}${rate.toFixed(2)}%`;
}

export function MarketSummaryBanner() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['market-reports', 'latest'],
    queryFn: () => get<{ report: MarketReport }>('/market-reports/latest'),
    staleTime: 60_000,
    retry: false,
  });

  if (isLoading || isError || !data) return null;

  const report = data.report;
  const meta = CATEGORY_META[report.category];
  if (!meta) return null;

  const indices = meta.tickers
    .map((t) => parseIndex(report.title, t))
    .filter((p): p is ParsedIndex => p !== null);

  return (
    <Link
      to={`/market/${report.id}`}
      className="group mb-6 flex w-full min-w-0 items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-r from-[var(--color-primary)] to-[#1e3a8a] px-4 py-3 text-white shadow-sm transition-opacity duration-200 hover:opacity-90 sm:px-5 sm:py-4 md:mb-10"
    >
      {/* 좌측 본문 — 3줄 구성: 카테고리 / 지수 / 요약 */}
      <div className="min-w-0 flex-1 overflow-hidden">
        {/* 1줄 — 카테고리 라벨 ("시장상황 · {라벨}") */}
        <div className="text-sm font-bold sm:text-base">
          <span className="opacity-80">시장상황</span>
          <span className="mx-1.5 opacity-60">·</span>
          <span>{meta.label}</span>
        </div>

        {/* 2줄 — 지수 (없으면 생략) */}
        {indices.length > 0 && (
          <div className="mt-1 min-w-0 truncate whitespace-nowrap overflow-hidden text-xs sm:text-sm">
            {indices.map((idx, i) => (
              <span key={idx.ticker} className={i > 0 ? 'ml-2 sm:ml-3' : ''}>
                {i > 0 && <span className="mr-2 opacity-60 sm:mr-3">·</span>}
                <span className="font-semibold">{idx.ticker}</span>{' '}
                <span className="tabular-nums">{idx.value}</span>{' '}
                <span className={`tabular-nums font-semibold ${rateColor(idx.rate)}`}>
                  {formatRate(idx.rate)}
                </span>
              </span>
            ))}
          </div>
        )}

        {/* 3줄 — 요약 */}
        <div className="mt-1 min-w-0 truncate whitespace-nowrap overflow-hidden text-xs opacity-90 sm:text-sm">
          {report.summary}
        </div>
      </div>

      {/* 우측 자세히 보기 — 모바일에서는 화살표만 노출 */}
      <span className="flex shrink-0 items-center gap-0.5 text-xs font-semibold sm:text-sm">
        <span className="hidden sm:inline">자세히 보기</span>
        <ChevronRight className="h-4 w-4" />
      </span>
    </Link>
  );
}
